import { useEffect, useMemo, useState } from "react";
import { SearchSmIcon, XCloseIcon } from "@vetty/icons";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Input,
  Select,
  Tabs,
  TabsItem,
  Textarea,
} from "@/components";
import { axiosInstance, type ApiResponse } from "@/helpers/axios";
import type { Account, AccountsMeta } from "@/app/accounts/_types/accounts.types";
import type {
  Group,
  GroupKind,
  GroupLicense,
  GroupMember,
} from "../../_types/groups.types";
import { UtcDateTimeField } from "./UtcDateTimeField.comp";
import {
  GROUP_FEATURE_META,
  GROUP_MODEL_FEATURE_KEYS,
  GROUP_PRODUCT_FEATURE_META,
  LICENSE_STATUS_OPTIONS,
  buildGroupAggregateBody,
  clearFeaturePolicy,
  fieldElementId,
  formatApiError,
  getAllModelsCheckboxState,
  licenseStatusFromLabelOrValue,
  setAllModelsSelected,
  toUtcDateTimeLocal,
  type GroupAggregateBody,
  type GroupFormTab,
  type GroupLicenseStatusOption,
} from "./group-form-drawer.helpers";
import styles from "./GroupFormDrawer.module.scss";

type GroupDetailPayload = {
  group: Group;
  features: string[];
  license: GroupLicense | null;
  members: GroupMember[];
};

type GroupFormDrawerProps = {
  mode: "create" | "edit";
  groupId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type SelectedAccountChip = {
  id: string;
  label: string;
};

const EMPTY_META: AccountsMeta = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};

const TAB_LABELS: Record<GroupFormTab, string> = {
  identity: "Identity",
  features: "Feature Policy",
  license: "License",
  accounts: "Accounts",
};

export const GroupFormDrawer = ({
  mode,
  groupId = null,
  isOpen,
  onClose,
  onSaved,
}: GroupFormDrawerProps) => {
  const [tab, setTab] = useState<GroupFormTab>("identity");
  const [invalidTab, setInvalidTab] = useState<GroupFormTab | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<GroupKind>("user");
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

  const [licenseEnabled, setLicenseEnabled] = useState(false);
  const [confirmRemoveLicense, setConfirmRemoveLicense] = useState(false);
  const [hadLicenseOnLoad, setHadLicenseOnLoad] = useState(false);
  const [licenseStatus, setLicenseStatus] =
    useState<GroupLicenseStatusOption>("active");
  const [seatLimit, setSeatLimit] = useState("10");
  const [activeFromUtc, setActiveFromUtc] = useState("");
  const [expiresAtUtc, setExpiresAtUtc] = useState("");

  const [selectedAccounts, setSelectedAccounts] = useState<SelectedAccountChip[]>(
    [],
  );
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [searchItems, setSearchItems] = useState<Account[]>([]);
  const [searchMeta, setSearchMeta] = useState<AccountsMeta>(EMPTY_META);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const selectedIds = useMemo(
    () => selectedAccounts.map((a) => a.id),
    [selectedAccounts],
  );

  const modelsCheckboxState = getAllModelsCheckboxState(selectedFeatures);

  const resetCreateDefaults = () => {
    setTab("identity");
    setInvalidTab(null);
    setName("");
    setDescription("");
    setKind("user");
    setSelectedFeatures([]);
    setLicenseEnabled(false);
    setConfirmRemoveLicense(false);
    setHadLicenseOnLoad(false);
    setLicenseStatus("active");
    setSeatLimit("10");
    setActiveFromUtc(toUtcDateTimeLocal(Date.now()));
    setExpiresAtUtc("");
    setSelectedAccounts([]);
    setSearch("");
    setSearchDebounced("");
    setPage(1);
    setSearchItems([]);
    setSearchMeta(EMPTY_META);
    setSearchError(null);
    setLoadError(null);
    setSubmitError(null);
  };

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") {
      resetCreateDefaults();
      return;
    }
    if (!groupId) return;

    let cancelled = false;
    (async () => {
      setIsLoadingDetail(true);
      setLoadError(null);
      setSubmitError(null);
      setInvalidTab(null);
      setTab("identity");
      try {
        const { data } = await axiosInstance.get<ApiResponse<GroupDetailPayload>>(
          `/v2/groups/${groupId}`,
        );
        if (cancelled) return;
        const detail = data.data;
        if (!detail?.group) {
          setLoadError("Failed to load group details");
          return;
        }
        setName(detail.group.name);
        setDescription(detail.group.description ?? "");
        setKind(detail.group.kind);
        setSelectedFeatures(detail.features ?? []);
        const license = detail.license ?? null;
        setHadLicenseOnLoad(Boolean(license));
        setLicenseEnabled(Boolean(license));
        setConfirmRemoveLicense(false);
        setLicenseStatus((license?.status as GroupLicenseStatusOption) || "active");
        setSeatLimit(String(license?.seatLimit ?? 10));
        setActiveFromUtc(
          toUtcDateTimeLocal(license?.activeFrom ?? Date.now()),
        );
        setExpiresAtUtc(toUtcDateTimeLocal(license?.expiresAt ?? null));
        setSelectedAccounts(
          (detail.members ?? []).map((m) => ({
            id: m.accountId,
            label: m.email,
          })),
        );
      } catch {
        if (!cancelled) setLoadError("Failed to load group details");
      } finally {
        if (!cancelled) setIsLoadingDetail(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, groupId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const { data } = await axiosInstance.get<
          ApiResponse<Account[], AccountsMeta>
        >("/v2/accounts/customers", {
          params: {
            role: "all",
            subscription: "all",
            period: "all",
            ...(searchDebounced ? { q: searchDebounced } : {}),
            page,
            limit: 10,
          },
        });
        if (cancelled) return;
        setSearchItems(data.data ?? []);
        setSearchMeta(data.meta ?? { ...EMPTY_META, page, limit: 10 });
      } catch {
        if (!cancelled) {
          setSearchError("Could not load accounts");
          setSearchItems([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, searchDebounced, page]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const toggleFeature = (key: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const addAccount = (account: Account) => {
    setSelectedAccounts((prev) => {
      if (prev.some((p) => p.id === account.id)) return prev;
      return [
        ...prev,
        {
          id: account.id,
          label: account.email || `${account.firstName} ${account.lastName}`.trim(),
        },
      ];
    });
  };

  const removeAccount = (id: string) => {
    setSelectedAccounts((prev) => prev.filter((a) => a.id !== id));
  };

  const setLicenseEnabledSafe = (next: boolean) => {
    if (!next && licenseEnabled) {
      setLicenseEnabled(false);
      setConfirmRemoveLicense(false);
      return;
    }
    setConfirmRemoveLicense(false);
    setLicenseEnabled(next);
    if (next && !activeFromUtc) {
      setActiveFromUtc(toUtcDateTimeLocal(Date.now()));
    }
  };

  const focusInvalidField = (field?: string) => {
    const id = fieldElementId(field);
    if (!id) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el && "focus" in el) {
        (el as HTMLElement).focus();
      }
    });
  };

  const handleSubmit = async () => {
    if (hadLicenseOnLoad && !licenseEnabled && !confirmRemoveLicense) {
      setInvalidTab("license");
      setTab("license");
      setSubmitError(
        "Confirm removing the seat-pool license. Existing members remain attached.",
      );
      return;
    }

    const built = buildGroupAggregateBody({
      name,
      description,
      kind,
      features: selectedFeatures,
      licenseEnabled,
      activeFromUtc,
      expiresAtUtc,
      status: licenseStatus,
      seatLimit,
      accountIds: selectedIds,
    });
    if (!built.ok) {
      setInvalidTab(built.tab);
      setTab(built.tab);
      setSubmitError(built.message);
      focusInvalidField(built.field);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setInvalidTab(null);
    try {
      const body: GroupAggregateBody = built.body;
      if (mode === "create") {
        await axiosInstance.post("/v2/groups", body);
      } else {
        await axiosInstance.put(`/v2/groups/${groupId}`, body);
      }
      onSaved();
      onClose();
    } catch (err) {
      setSubmitError(
        formatApiError(
          err,
          mode === "create" ? "Failed to create group" : "Failed to save group",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = mode === "create" ? "Create group" : "Edit group";
  const busy = isSubmitting || isLoadingDetail;

  const modelFeatures = GROUP_FEATURE_META.filter((f) => f.group === "models");

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel={title}>
      <DrawerHeader title={title} onClose={handleClose} closeDisabled={busy} />
      <form
        className={styles["comp__stack"]}
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <DrawerBody className={styles["comp__body"]}>
          <div className={styles["comp"]}>
            {loadError ? <p className={styles["comp__error"]}>{loadError}</p> : null}

            <div className={styles["comp__tabs-wrap"]}>
              <Tabs
                value={tab}
                onChange={(next) => setTab(next as GroupFormTab)}
                variant="secondary"
                size="medium"
              >
                {(Object.keys(TAB_LABELS) as GroupFormTab[]).map((value) => (
                  <TabsItem
                    key={value}
                    value={value}
                    className={
                      invalidTab === value ? styles["comp__tab-error"] : undefined
                    }
                    endContent={
                      invalidTab === value ? (
                        <span aria-hidden>•</span>
                      ) : undefined
                    }
                  >
                    {TAB_LABELS[value]}
                  </TabsItem>
                ))}
              </Tabs>
            </div>

            {tab === "identity" ? (
              <div className={styles["comp__panel"]} role="tabpanel">
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>Group identity</h3>
                  <p className={styles["comp__subtitle"]}>
                    Set the name, kind, and optional description for this group.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__fields"]}>
                    <Input
                      id="group-form-name"
                      label="Name"
                      value={name}
                      onChange={setName}
                      maxLength={120}
                      required
                      disabled={busy}
                    />
                    <Select
                      id="group-form-kind"
                      label="Kind"
                      value={kind}
                      onChange={(next) => setKind(next as GroupKind)}
                      disabled={busy}
                      variant="secondary"
                      fullWidth
                    >
                      <option value="user">User</option>
                      <option value="device">Device</option>
                      <option value="mixed">Mixed</option>
                    </Select>
                    <Textarea
                      id="group-form-description"
                      label="Description"
                      value={description}
                      onChange={setDescription}
                      maxLength={2000}
                      rows={3}
                      disabled={busy}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {tab === "features" ? (
              <div className={styles["comp__panel"]} role="tabpanel">
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>
                    Product feature policy
                  </h3>
                  <p className={styles["comp__subtitle"]}>
                    Choose product features independently of the seat pool.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__feature-actions"]}>
                    <Button
                      type="button"
                      size="small"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setSelectedFeatures(clearFeaturePolicy())}
                    >
                      Clear all
                    </Button>
                  </div>

                  <div className={styles["comp__feature-group"]}>
                    <p className={styles["comp__feature-group-title"]}>Models</p>
                    <Checkbox
                      id="group-form-feature-all-models"
                      checked={modelsCheckboxState}
                      onChange={(checked) =>
                        setSelectedFeatures((prev) =>
                          setAllModelsSelected(prev, checked),
                        )
                      }
                      disabled={busy}
                    >
                      <Checkbox.Label>
                        All models ({GROUP_MODEL_FEATURE_KEYS.length})
                      </Checkbox.Label>
                    </Checkbox>
                    <div className={styles["comp__feature-grid"]}>
                      {modelFeatures.map((feature) => (
                        <div
                          key={feature.key}
                          className={styles["comp__feature-option"]}
                        >
                          <Checkbox
                            id={`group-form-feature-${feature.key}`}
                            checked={selectedFeatures.includes(feature.key)}
                            onChange={() => toggleFeature(feature.key)}
                            disabled={busy}
                          >
                            <Checkbox.Label>{feature.label}</Checkbox.Label>
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </div>

                  <hr
                    className={styles["comp__feature-divider"]}
                    role="separator"
                  />

                  <div className={styles["comp__feature-group"]}>
                    <p className={styles["comp__feature-group-title"]}>Product</p>
                    <div className={styles["comp__feature-grid"]}>
                      {GROUP_PRODUCT_FEATURE_META.map((feature) => (
                        <div
                          key={feature.key}
                          className={styles["comp__feature-option"]}
                        >
                          <Checkbox
                            id={`group-form-feature-${feature.key}`}
                            checked={selectedFeatures.includes(feature.key)}
                            onChange={() => toggleFeature(feature.key)}
                            disabled={busy}
                          >
                            <Checkbox.Label>{feature.label}</Checkbox.Label>
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === "license" ? (
              <div className={styles["comp__panel"]} role="tabpanel">
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>Seat-pool license</h3>
                  <p className={styles["comp__subtitle"]}>
                    Optionally reserve seats; membership without a license stays valid.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__license-toggle"]}>
                    <Checkbox
                      id="group-form-license-enabled"
                      checked={licenseEnabled}
                      onChange={(checked) => setLicenseEnabledSafe(checked)}
                      disabled={busy}
                    >
                      <Checkbox.Label>Enable seat-pool license</Checkbox.Label>
                    </Checkbox>
                    {!licenseEnabled && (hadLicenseOnLoad || confirmRemoveLicense) ? (
                      <div className={styles["comp__warning"]} role="status">
                        <p>
                          Removing the seat pool keeps existing members attached with no
                          seat capacity. Confirm below before save — license null removes
                          the pool.
                        </p>
                        {hadLicenseOnLoad ? (
                          <Checkbox
                            id="group-form-confirm-remove-license"
                            checked={confirmRemoveLicense}
                            onChange={setConfirmRemoveLicense}
                            disabled={busy}
                          >
                            <Checkbox.Label>
                              Confirm remove seat pool (members remain)
                            </Checkbox.Label>
                          </Checkbox>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {licenseEnabled ? (
                    <div className={styles["comp__fields"]}>
                      <div className={styles["comp__row"]}>
                        <Select
                          id="group-form-license-status"
                          label="Status"
                          value={licenseStatus}
                          onChange={(next) => {
                            const mapped = licenseStatusFromLabelOrValue(next);
                            if (mapped) setLicenseStatus(mapped);
                          }}
                          disabled={busy}
                          variant="secondary"
                          fullWidth
                        >
                          {LICENSE_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        <Input
                          id="group-form-seat-limit"
                          label="Seat limit"
                          value={seatLimit}
                          onChange={setSeatLimit}
                          inputMode="numeric"
                          disabled={busy}
                        />
                      </div>
                      <UtcDateTimeField
                        id="group-form-active-from"
                        label="Active from (UTC)"
                        value={activeFromUtc}
                        onChange={setActiveFromUtc}
                        disabled={busy}
                        required
                      />
                      <UtcDateTimeField
                        id="group-form-expires-at"
                        label="Expires at (UTC)"
                        value={expiresAtUtc}
                        onChange={setExpiresAtUtc}
                        disabled={busy}
                        optional
                      />
                      <p className={styles["comp__hint"]}>
                        Selected members: {selectedIds.length}
                        {licenseEnabled ? ` · capacity ${seatLimit || "—"}` : ""}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {tab === "accounts" ? (
              <div
                className={`${styles["comp__panel"]} ${styles["comp__panel--accounts"]}`}
                role="tabpanel"
              >
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>
                    Assign existing accounts
                  </h3>
                  <p className={styles["comp__subtitle"]}>
                    Search and attach existing accounts without changing their roles.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__accounts-top"]}>
                    {selectedAccounts.length > 0 ? (
                      <div className={styles["comp__chips"]}>
                        {selectedAccounts.map((account) => (
                          <span key={account.id} className={styles["comp__chip"]}>
                            <span className={styles["comp__chip-label"]}>
                              {account.label}
                            </span>
                            <button
                              type="button"
                              className={styles["comp__chip-remove"]}
                              aria-label={`Remove ${account.label}`}
                              onClick={() => removeAccount(account.id)}
                              disabled={busy}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className={styles["comp__empty"]}>No accounts selected.</p>
                    )}

                    <Input
                      id="group-form-account-search"
                      label="Search accounts"
                      value={search}
                      onChange={(value) => {
                        setSearch(value);
                        setPage(1);
                      }}
                      placeholder="Email, name…"
                      disabled={busy}
                      startContent={
                        <SearchSmIcon style={{ width: 18, height: 18 }} aria-hidden />
                      }
                      endContent={
                        search ? (
                          <button
                            type="button"
                            className={styles["comp__clear-search"]}
                            aria-label="Clear search"
                            onClick={() => {
                              setSearch("");
                              setPage(1);
                            }}
                            disabled={busy}
                          >
                            <XCloseIcon style={{ width: 16, height: 16 }} />
                          </button>
                        ) : null
                      }
                    />

                    {searchError ? (
                      <p className={styles["comp__error"]}>{searchError}</p>
                    ) : null}
                    {searchLoading ? (
                      <p className={styles["comp__empty"]}>Loading accounts…</p>
                    ) : null}
                    {!searchLoading && !searchError && searchItems.length === 0 ? (
                      <p className={styles["comp__empty"]}>
                        No accounts match this search.
                      </p>
                    ) : null}
                  </div>

                  <div className={styles["comp__search-results"]}>
                    {searchItems.map((account) => {
                      const already = selectedIds.includes(account.id);
                      return (
                        <div key={account.id} className={styles["comp__result-card"]}>
                          <div>
                            <div>{account.email}</div>
                            <div className={styles["comp__result-meta"]}>
                              {account.firstName} {account.lastName} · {account.role}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="small"
                            variant="secondary"
                            disabled={busy || already}
                            onClick={() => addAccount(account)}
                          >
                            {already ? "Selected" : "Add"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  <div className={styles["comp__pager"]}>
                    <Button
                      type="button"
                      size="small"
                      variant="secondary"
                      disabled={busy || page <= 1 || searchLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className={styles["comp__result-meta"]}>
                      Page {searchMeta.page} / {Math.max(1, searchMeta.totalPages)}
                    </span>
                    <Button
                      type="button"
                      size="small"
                      variant="secondary"
                      disabled={
                        busy ||
                        searchLoading ||
                        searchMeta.page >= Math.max(1, searchMeta.totalPages)
                      }
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}

            {submitError ? <p className={styles["comp__error"]}>{submitError}</p> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy || Boolean(loadError)}>
            {isSubmitting
              ? mode === "create"
                ? "Creating…"
                : "Saving…"
              : mode === "create"
                ? "Create"
                : "Save"}
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};
